import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { toLocalTimestampLiteral } from 'Data/format.js'
import { useMutation, useClient } from 'Data/Api'
import { useDataValue } from 'Simple/Data'

import mutationUpdateTaskAssignee from './mutation-update-task-assignee.graphql.js'
import mutationDeleteTaskAssignee from './mutation-delete-task-assignee.graphql.js'
import mutationUpdateTaskPlacement from './mutation-update-task-placement.graphql.js'
import mutationUpdateTaskStage from './mutation-update-task-stage.graphql.js'
import mutationUpdateTaskPriority from './mutation-update-task-priority.graphql.js'
import mutationUpdateTaskDueDate from './mutation-update-task-due-date.graphql.js'
import mutationCreateTaskComment from './mutation-create-task-comment.graphql.js'
import mutationDeleteTask from './mutation-delete-task.graphql.js'
import queryGetTaskStageDone from './query-get-task-stage-done.graphql.js'
import mutationCreateStageSubscriber from './mutation-create-stage-subscriber.graphql.js'
import mutationDeleteStageSubscriber from './mutation-delete-stage-subscriber.graphql.js'
import mutationUpdateBoardStatus from './mutation-update-board-status.graphql.js'
import createTaskMutation from './mutation-create-task.graphql.js'

import { FREQUENTLY_USED_VALUE_KEYS } from 'Data/constants.js'
import { setFrequentlyUsedSelectionValue } from 'Logic/localStorage.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, notify] = useNotifications()
  let client = useClient()

  let [, executeMutationCreateTask] = useMutation(createTaskMutation)

  let [, executeMutationUpdateTaskAssignee] = useMutation(
    mutationUpdateTaskAssignee
  )
  let [, executeMutationDeleteTaskAssignee] = useMutation(
    mutationDeleteTaskAssignee
  )
  let [, executeMutationUpdateTaskPlacement] = useMutation(
    mutationUpdateTaskPlacement
  )
  let [, executeMutationUpdateTaskStage] = useMutation(mutationUpdateTaskStage)

  let [, executeMutationUpdateTaskPriority] = useMutation(
    mutationUpdateTaskPriority
  )
  let [, executeMutationUpdateTaskDueDate] = useMutation(
    mutationUpdateTaskDueDate
  )
  let [, executeMutationCreateTaskComment] = useMutation(
    mutationCreateTaskComment
  )
  let [, executeMutationDeleteTask] = useMutation(mutationDeleteTask)
  let [, executeMutationCreateStageSubscriber] = useMutation(
    mutationCreateStageSubscriber
  )
  let [, executeMutationDeleteStageSubscriber] = useMutation(
    mutationDeleteStageSubscriber
  )
  let [, executeMutationUpdateBoardStatus] = useMutation(
    mutationUpdateBoardStatus
  )

  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location._id',
  })

  let current_user_id = useDataValue({
    context: 'global',
    path: 'user.id',
    viewPath: props.viewPath,
  })

  let xid = useDataValue({
    context: 'global',
    path: 'current_location.xid',
    viewPath: props.viewPath,
  })

  return async function onSubmit({ args, value, change }) {
    switch (args.type) {
      case 'create_task': {
        let {
          error,
          data: { insert_tasks_one },
        } = await executeMutationCreateTask({
          location_id: current_location_id,
          order: Date.now(),
          board_id: args.value.board_id,
          stage_id: args.value.stage_id,
          title: args.value.title,
          description: args.value.description,
          priority: args.value.priority,
          due_date:
            args.value.due_date && toLocalTimestampLiteral(args.value.due_date),
          resource_schema: args.value.patient?._id ? 'vaxiom' : null,
          resource_table: args.value.patient?._id ? 'patients' : null,
          resource_id: args.value.patient?._id,
          assignees: args.value.assignee?.id
            ? [{ user_id: args.value.assignee?.id }]
            : [],
          task_tags: args.value.tags.map(tag => ({
            tag_id: tag.id,
          })),
          resources: args.value.action?.id
            ? [
                {
                  type: args.value.action.id,
                  resource_id: args.value.action.document_template,
                  resource_schema: 'vaxiom',
                  resource_table: 'document_templates',
                  metadata: {
                    recipient_id: args.value.action.recipient_id,
                    postal_address_id: args.value.action.postal_address_id,
                  },
                },
              ]
            : [],
        })

        if (error) {
          console.error('Error creating task:', error)
          props.onError?.(error)
          return
        }

        props.onSave?.(insert_tasks_one.id)

        if (
          args.value.assignee?.id &&
          args.value.assignee?.id !== current_user_id
        ) {
          setFrequentlyUsedSelectionValue({
            key: FREQUENTLY_USED_VALUE_KEYS.TASK_ASSIGNEES,
            value: args.value.assignee,
            xid,
          })
        }

        change(next => {
          next.selected.new_task_overlay = {
            open: false,
          }
        })
        break
      }

      case 'open_new_task_overlay':
        change(next => {
          next.selected.new_task_overlay = {
            open: true,
            form: {
              board_id: value.selected.board_id,
              stage_id: args.stage_id || null,
            },
          }
        })
        break

      case 'close_new_task_overlay':
        change(next => {
          next.selected.new_task_overlay = {
            open: false,
          }
        })
        break

      case 'open_task_overlay':
        change(next => {
          next.selected.task_overlay = {
            open: true,
            task_id: args.task_id,
            show_board: false,
          }
        })
        break

      case 'update_board_status': {
        let { error } = await executeMutationUpdateBoardStatus({
          board_id: args.board_id,
          status: args.status,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the board. Please try again later.'
            )
          )
          return
        }

        notify(
          notifySuccess(
            `Board ${args.status === 'active' ? 'unarchived' : 'archived'}`
          )
        )

        break
      }

      case 'delete_task': {
        let { error } = await executeMutationDeleteTask({
          id: args.id,
        })

        if (error) {
          console.error('Error deleting task:', error)
          notify(notifyError('Cannot delete task due to an error'))
          return
        }

        notify(notifySuccess(`Task deleted.`))

        change(next => {
          next.selected.task_overlay = {
            open: false,
            assignee_id: null,
            priority: 'medium',
            patient_id: null,
            title: '',
            description: null,
          }
        })
        break
      }

      case 'set_task_assignee': {
        let { error } = await executeMutationUpdateTaskAssignee({
          task_id: args.task_id,
          user_id: args.user_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the assignee. Please try again later.'
            )
          )
          return
        }

        if (args.person && args.person?.id !== current_user_id) {
          setFrequentlyUsedSelectionValue({
            key: FREQUENTLY_USED_VALUE_KEYS.TASK_ASSIGNEES,
            value: args.person,
            xid,
          })
        }

        break
      }

      case 'delete_task_assignee': {
        let { error } = await executeMutationDeleteTaskAssignee({
          task_id: args.task_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while deleting the assignee. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_placement': {
        let { error } = await executeMutationUpdateTaskPlacement({
          task_id: args.task_id,
          stage_id: args.stage_id,
          order: args.order,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the placement. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_priority': {
        let { error } = await executeMutationUpdateTaskPriority({
          task_id: args.task_id,
          priority: args.priority,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the priority. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_due_date': {
        let { error } = await executeMutationUpdateTaskDueDate({
          task_id: args.task_id,
          due_date: args.due_date && toLocalTimestampLiteral(args.due_date),
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the due date. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'create_task_comment': {
        let { error } = await executeMutationCreateTaskComment({
          task_id: args.task_id,
          comment: args.comment,
        })

        if (error) {
          notify(
            notifyError(
              'Error while creating task comment. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'create_stage_subscriber': {
        let { error } = await executeMutationCreateStageSubscriber({
          stage_id: args.stage_id,
          board_id: value.selected.board_id,
          user_id: args.user_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while creating stage subscriber. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'delete_stage_subscriber': {
        let { error } = await executeMutationDeleteStageSubscriber({
          stage_id: args.stage_id,
          user_id: args.user_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while deleting stage subscriber. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_done': {
        let {
          data: {
            tasks_stages: [task_stage],
          },
        } = await client
          .query(
            queryGetTaskStageDone,
            { board_id: value.selected.board_id || args.board_id },
            { requestPolicy: 'network-only' }
          )
          .toPromise()

        if (!task_stage) {
          notify(notifyError('Cannot find "done" the stage for this board.'))
          return
        }

        let { error } = await executeMutationUpdateTaskStage({
          task_id: args.task_id,
          stage_id: task_stage.id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while setting task done. Please try again later.'
            )
          )
          return
        }

        notify(notifySuccess(`Task set to done.`))

        break
      }

      case 'set_task_stage': {
        let { error } = await executeMutationUpdateTaskStage({
          task_id: args.task_id,
          stage_id: args.stage_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the stage. Please try again later.'
            )
          )
          return
        }

        notify(notifySuccess(`Stage has been changed.`))

        break
      }

      case 'set_bulk_tasks_stage': {
        await Promise.all(
          args.task_and_stage_ids.map(async task => {
            let { error } = await executeMutationUpdateTaskStage({
              task_id: task.id,
              stage_id: task.stage_id,
            })

            if (error) {
              notify(
                notifyError(
                  `Error while changing the stage. Task id #${task.id}. Please try again later.`
                )
              )
            }
          })
        )

        notify(notifySuccess(`Stages have been changed.`))

        break
      }

      case 'set_bulk_tasks_assignee': {
        await Promise.all(
          args.task_and_assignee_ids.map(async task => {
            if (task.user_id === 'delete') {
              let { error } = await executeMutationDeleteTaskAssignee({
                task_id: task.id,
              })
              if (error) {
                notify(
                  notifyError(
                    `Error while deleting the assignee. Task id #${task.id}. Please try again later.`
                  )
                )
              }
            } else {
              let { error } = await executeMutationUpdateTaskAssignee({
                task_id: task.id,
                user_id: task.user_id,
              })
              if (error) {
                notify(
                  notifyError(
                    `Error while changing the assignee. Task id #${task.id}. Please try again later.`
                  )
                )
              }
            }
          })
        )

        notify(notifySuccess(`Assignees have been changed.`))

        break
      }

      case 'set_bulk_tasks_due_date': {
        await Promise.all(
          args.task_and_due_dates.map(async task => {
            let { error } = await executeMutationUpdateTaskDueDate({
              task_id: task.id,
              due_date: task.due_date,
            })

            if (error) {
              notify(
                notifyError(
                  `Error while changing the due date. Task id #${task.id}. Please try again later.`
                )
              )
            }
          })
        )

        notify(notifySuccess(`Due dates have been changed.`))

        break
      }

      case 'set_bulk_tasks_priority': {
        await Promise.all(
          args.task_and_priorities.map(async task => {
            let { error } = await executeMutationUpdateTaskPriority({
              task_id: task.id,
              priority: task.priority,
            })

            if (error) {
              notify(
                notifyError(
                  `Error while changing the priority. Task id #${task.id}. Please try again later.`
                )
              )
            }
          })
        )

        notify(notifySuccess(`Priorities have been changed.`))

        break
      }

      default:
    }
  }
}
